import { Variable, not, or, if as _if } from 'alkali'
import { Section, Table, TR, TD, TH, Div, Button, Span, ElementProperties } from 'alkali'
import { ExtractField } from 'components/extract/Inputs'
import StudyDesignButton from 'components/extract/StudyDesignButton'
import StudySummaryModel from 'components/extract/model/StudySummaryModel'
import { FocusedState } from 'components/extract/ExtractAnnotationModel'
import { submitExtractions, validStudyN } from './model/extraction'
import UpdateModel, { canSubmit as _canSubmit, composeExtractionRequest } from './model/UpdateModel'
import ExtractionModel from './model/ExtractionModel'
import OutcomesModel from './model/OutcomesModel'
import OutcomesMgmtTable from './OutcomesMgmtTable'
import GroupsModel from './model/GroupsModel'
import GroupsMgmtTable from './GroupsMgmtTable'
import NotesModel from './model/NotesModel'
import NotesMgmtTable from './NotesMgmtTable'
import Tabbed from 'components/generic/Tabbed'
import Icon from 'components/Icon'
import * as loadingIcon from 'icons/loading-half.svg'
import './MetadataPane.less'
import TextInputControl from 'components/generic/TextInputControl'

interface MetadataPaneOpts extends ElementProperties<MetadataPane> {
	extractionModel: ExtractionModel
	focusedState?: FocusedState
	editStudy?: boolean
}

class MetadataTable extends Table.with('.meta-table') {
	created(props) {
		props.content = [
			TR([
				TH({ attributes: { scope: 'row' } }, 'Study N'),
				TD([
					new ExtractField('.group-n', {
						id: 'study-n',
						ftype: 'group-n',
						val: props.abstractSource.N,
						validation: props.abstractSource.N.to(n => validStudyN(n)),
						annot: new Variable(),
						advanced: new Variable(),
						optional: true,
						focusedState: props.focusedState
					})
				])
			]),
			TR([
				TH({ attributes: { scope: 'row' } }, 'Study Design'),
				TD([new StudyDesignButton({ designs: props.abstractSource.Designs })])
			]),
			TR([
				TH({ attributes: { scope: 'row' } }, 'Acronym'),
				TD([
					TextInputControl({
						id: 'study-acronym',
						placeholder: 'ACRONYM',
						value: props.abstractSource.Acronym,
						class: 'study-acronym'
					})
				])
			]),
		]
	}
}

interface SubmitButtonOpts extends ElementProperties<SubmitButton> {
	summary: StudySummaryModel
	outcomesModel: OutcomesModel
}

class SubmitButton extends Button('.submit-extraction').with({ type: 'button' }) {
	created(props: SubmitButtonOpts) {
		const busy = new Variable(false)
		const { summary, models, error } = props

		const canSubmit = _canSubmit(summary, ...models)
		Object.assign(props, {
			title: 'Submit',
			disabled: or(not(canSubmit), busy),
			classes: {
				disabled: or(not(canSubmit), busy)
			},
			onclick(e) {
				e.stopPropagation()
				busy.put(true)
				const req = composeExtractionRequest(summary.effectiveStudy, summary.studyCtx.user, summary, ...models)
				if (req) {
					Promise.resolve(
						submitExtractions(req, summary.onSubmit)
					).then(() =>
						error.put(null)
					).catch(e => {
						error.put({
							text: e.toString(),
							error: e
						})
					}).finally(() => busy.put(false))
				}
			}
		})
		props.content = _if(busy, [[Span('Saving...'), Icon('.spin-icon', { title: 'Saving...', icon: loadingIcon })], ['Submit']])
	}
}

/**
 * Contains forms for editing the study metadata (Group N, Design), and separate sub-tabs for managing
 * notes, groups, and rows.
 */
export default class MetadataTab extends Div('.meta-tab') {
	models: UpdateModel[]
	created(props: MetadataPaneOpts) {
		const pane = new MetadataPane({ extractionModel: props.extractionModel, focusedState: props.focusedState, editStudy: props.editStudy })
		this.models = pane.models
		props.content = [pane]
	}
}

class MetadataPane extends Section('.meta-pane') {
	models: UpdateModel[]
	created(props: MetadataPaneOpts) {
		const { extractionModel, focusedState, editStudy } = props
		const { summary } = extractionModel
		const { abstractSource } = summary
		const error = new Variable()
		const outcomesModel = new OutcomesModel(extractionModel.study)
		const groupsModel = new GroupsModel(extractionModel.study)
		const notesModel = new NotesModel(extractionModel.study)
		const models = this.models = [notesModel, outcomesModel, groupsModel]
		if (editStudy) this.models.unshift(summary)
		props.content = [
			editStudy != false ? new MetadataTable({ abstractSource, focusedState }) : undefined,
			new Tabbed({
				leading: Div('.leading')
			}, [{
				label: 'Notes',
				content: new NotesMgmtTable({ notesModel })
			}, {
				label: 'Outcomes',
				content: new OutcomesMgmtTable({ summaryModel: summary, outcomesModel })
			}, {
				label: 'Groups',
				content: new GroupsMgmtTable({ groupsModel })
			}]),
			Div('.message-bar', [
				Div('.error-msg', { classes: { hidden: not(error) } }, error.to(e =>
					e ? (e.content || Span({ title: e.error || e.text }, e.text)) : ''
				))
			]),
			summary.study ? Div('.submission-bar', [
				new SubmitButton({ summary, models, error })
			]) : undefined
		]
	}
}